import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  api,
  type Me, type PublicSnapshot, type DashboardSummary, type NotificationRow,
  type SpokeRow, type QaFinding, type LgwCert, type CubeDeviceCert, type WxcStatus, type WxccStatus,
  type LgwGateway, type WxccQueue, type FeatureRequest, type SupportTicket, type TenantRow, type LicenseRow,
  type ConnectorHealth, type WebexServiceStatus,
} from '../lib/api';

// ===========================================================================
//  Operations Dashboard — chart-heavy, every module gets a card.
//  Built for BUILD-TASK-dashboard. Live data only. Honest empties.
//  Layout (top → bottom):
//    1. Header + N-of-M health banner
//    2. KPI strip (4 tiles + sparklines)
//    3. Chart row: call-volume area + system-health donut
//    4. Module grid (18 cards w/ status dots + live metric)
//    5. Bottom: top destinations bar + recent alerts feed
// ===========================================================================

type ModuleStatus = 'healthy' | 'attention' | 'down' | 'unknown';
interface ModuleCard {
  key: string;
  label: string;
  to: string;
  group: string;
  metric: string;       // live key value or honest empty
  status: ModuleStatus;
  helper?: string;      // small secondary line
}

// Resolve the tenant to query — non-super-admins use their own. Super-admins
// honor the tnh_admin_tenant localStorage value set by the other module pages.
function resolveQueryTenant(me: Me, override: string): string {
  if (me.role !== 'super_admin') return me.tenant_id;
  return override || me.tenant_id;
}

export default function Dashboard({ me }: { me: Me }) {
  // tenant picker for super-admin (shared with other module pages)
  const [adminTenant, setAdminTenant] = useState<string>(() => {
    const stored = localStorage.getItem('tnh_admin_tenant');
    if (stored != null) return stored;
    return me.tenant_id !== 'GLOBAL' ? me.tenant_id : '';
  });
  useEffect(() => { localStorage.setItem('tnh_admin_tenant', adminTenant); }, [adminTenant]);
  const tenantId = resolveQueryTenant(me, adminTenant);
  const canQueryTenant = tenantId && tenantId !== 'GLOBAL';

  // ── live data buckets ────────────────────────────────────────────────────
  const [snap, setSnap]               = useState<PublicSnapshot | null>(null);
  const [summary, setSummary]         = useState<DashboardSummary | null>(null);
  const [alerts, setAlerts]           = useState<NotificationRow[] | null>(null);
  const [unread, setUnread]           = useState<number | null>(null);
  const [spokes, setSpokes]           = useState<SpokeRow[] | null>(null);
  const [lgwCerts, setLgwCerts]       = useState<LgwCert[] | null>(null);
  const [cubeCerts, setCubeCerts]     = useState<CubeDeviceCert[] | null>(null);
  const [qa, setQa]                   = useState<QaFinding[] | null>(null);
  const [wxc, setWxc]                 = useState<WxcStatus | null>(null);
  const [wxcc, setWxcc]               = useState<WxccStatus | null>(null);
  const [gateways, setGateways]       = useState<LgwGateway[] | null>(null);
  const [queues, setQueues]           = useState<WxccQueue[] | null>(null);
  const [features, setFeatures]       = useState<FeatureRequest[] | null>(null);
  const [tickets, setTickets]         = useState<SupportTicket[] | null>(null);
  const [tenants, setTenants]         = useState<TenantRow[] | null>(null);
  const [licenses, setLicenses]       = useState<LicenseRow[] | null>(null);
  const [connectors, setConnectors]   = useState<ConnectorHealth[] | null>(null);
  const [webex, setWebex]             = useState<WebexServiceStatus | null>(null);
  const [err, setErr]                 = useState<string | null>(null);
  const [loadedAt, setLoadedAt]       = useState<number | null>(null);

  useEffect(() => {
    let cancel = false;
    const load = () => {
      // ANY failure on a single fetcher must not nuke the dashboard — wrap
      // each in a per-call catch so the others still populate.
      const safe = <T,>(p: Promise<T>): Promise<T | null> =>
        p.then((v) => v).catch(() => null);
      Promise.all([
        safe(api.status()),
        canQueryTenant ? safe(api.reportsDashboardSummary(tenantId)) : Promise.resolve(null),
        safe(api.notificationsList('unread', 8)),
        safe(api.notificationsUnreadCount()),
        canQueryTenant ? safe(api.spokesList(tenantId)) : Promise.resolve(null),
        safe(api.lgwCerts()),
        safe(api.lgwCubeCerts()),
        safe(api.qaFindings({ limit: 5 })),
        safe(api.wxcStatus(me.role === 'super_admin' ? adminTenant || undefined : undefined)),
        safe(api.wxccStatus()),
        safe(api.lgwGateways()),
        safe(api.wxccQueues()),
        safe(api.featuresList({ status: 'pending_review', limit: 50 })),
        safe(api.ticketsList({ status: 'awaiting_approval' })),
        me.role === 'super_admin' ? safe(api.tenantsList()) : Promise.resolve(null),
        canQueryTenant ? safe(api.tenantsListLicenses(tenantId)) : Promise.resolve(null),
        safe(api.radarConnectors()),
      ]).then(([s, sum, a, u, sp, lc, cc, qf, wxcS, wxccS, gw, qs, ft, tk, tn, lic, cn]) => {
        if (cancel) return;
        setSnap(s);
        setSummary(sum);
        setAlerts(a?.notifications ?? null);
        setUnread(u?.unread ?? null);
        setSpokes(sp?.spokes ?? null);
        setLgwCerts(lc?.certs ?? null);
        setCubeCerts(cc?.certs ?? null);
        setQa(qf?.findings ?? null);
        setWxc(wxcS);
        setWxcc(wxccS);
        setGateways(gw?.gateways ?? null);
        setQueues(qs?.queues ?? null);
        setFeatures(ft?.features ?? null);
        setTickets(tk?.tickets ?? null);
        setTenants(tn?.tenants ?? null);
        setLicenses(lic?.licenses ?? null);
        setConnectors(cn?.connectors ?? null);
        setLoadedAt(Date.now());
        setErr(null);
      }).catch((e) => { if (!cancel) setErr(e?.message ?? 'load failed'); });
    };
    load();
    const t = setInterval(load, 60_000);  // refresh every minute
    return () => { cancel = true; clearInterval(t); };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [tenantId]);

  // Webex platform service status (status.webex.com watcher) — independent of tenant.
  useEffect(() => {
    let cancel = false;
    const load = () => { api.webexStatus().then((w) => { if (!cancel) setWebex(w); }).catch(() => {}); };
    load();
    const t = setInterval(load, 60_000);
    return () => { cancel = true; clearInterval(t); };
  }, []);

  // ── derive module cards (each: live key metric + status dot) ─────────────
  const cards = useMemo<ModuleCard[]>(() => deriveModuleCards({
    me,
    snap, summary, alerts, unread, spokes, lgwCerts, cubeCerts, qa,
    wxc, wxcc, gateways, queues, features, tickets, tenants, licenses, connectors,
  }), [snap, summary, alerts, unread, spokes, lgwCerts, cubeCerts, qa, wxc, wxcc, gateways, queues, features, tickets, tenants, licenses, connectors]);

  // overall health rollup for the banner
  const healthy = cards.filter((c) => c.status === 'healthy').length;
  const attention = cards.filter((c) => c.status === 'attention').length;
  const down = cards.filter((c) => c.status === 'down').length;
  const unknown = cards.filter((c) => c.status === 'unknown').length;
  const overallTier: 'ok' | 'warn' | 'err' | 'unknown' =
    down > 0 ? 'err' : attention > 0 ? 'warn' : (healthy > 0 ? 'ok' : 'unknown');

  return (
    <div className="space-y-6">
      {/* ── 1. Header + health banner ───────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Operations Dashboard</h1>
          <p className="text-gray-500 text-sm mt-1">
            Signed in as <span className="font-mono">{me.email}</span> · tenant{' '}
            <span className="font-mono">{tenantId || 'GLOBAL'}</span>
            {loadedAt && <span className="ml-2 text-gray-400">· refreshed {new Date(loadedAt).toLocaleTimeString()}</span>}
          </p>
        </div>
        {me.role === 'super_admin' && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500">Tenant:</span>
            <input value={adminTenant} onChange={(e) => setAdminTenant(e.target.value)}
              placeholder="tenant id"
              className="px-3 py-1.5 text-sm rounded-md border border-tnh-border dark:border-tnh-border-dark bg-transparent w-40 font-mono" />
          </div>
        )}
      </div>

      <section className={`card p-4 border ${
        overallTier === 'err'  ? 'border-tnh-err/40 bg-tnh-err/5'
        : overallTier === 'warn' ? 'border-tnh-warn/40 bg-tnh-warn/5'
        : overallTier === 'ok'   ? 'border-tnh-ok/40  bg-tnh-ok/5'
        : 'border-tnh-border dark:border-tnh-border-dark'
      }`}>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <span className={`w-2.5 h-2.5 rounded-full ${
              overallTier === 'err' ? 'bg-tnh-err' : overallTier === 'warn' ? 'bg-tnh-warn' : overallTier === 'ok' ? 'bg-tnh-ok' : 'bg-gray-400'
            } animate-pulse`} />
            <div className="text-base font-semibold">
              <span className="text-tnh-ok">{healthy}</span> of {cards.length} modules healthy
            </div>
          </div>
          <div className="flex items-center gap-4 text-xs">
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-tnh-ok" /> {healthy} healthy</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-tnh-warn" /> {attention} attention</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-tnh-err" /> {down} down</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-gray-400" /> {unknown} unknown</span>
          </div>
        </div>
        {err && <div className="text-xs text-tnh-err mt-2">refresh error: {err}</div>}
      </section>

      {/* ── 2. KPI strip with sparklines ────────────────────────────────── */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiSpark label="Calls today" value={summary ? String(summary.totals.calls_today) : '…'}
                  sub="last 12h trend" tone="ok" spark={(summary?.hourly_series ?? []).map((h) => h.calls)} />
        <KpiSpark label="Active now (≤5min)" value={summary ? String(summary.totals.active_estimate) : '…'}
                  sub="CDR rows started in last 5m" tone="ok" spark={(summary?.hourly_series ?? []).slice(-6).map((h) => h.calls)} />
        <KpiSpark label="Avg handle time" value={summary ? fmtDur(summary.totals.avg_handle_today) : '…'}
                  sub="today (UTC)" tone="ok" spark={(summary?.hourly_series ?? []).map((h) => h.avg_handle || 0)} />
        <KpiSpark label="Open alerts" value={unread != null ? String(unread) : '…'}
                  sub={(unread ?? 0) > 0 ? 'unread notifications' : 'all clear'}
                  tone={(unread ?? 0) > 0 ? 'warn' : 'ok'}
                  spark={(summary?.hourly_series ?? []).map((h) => h.abandoned || 0)} />
      </section>

      {/* ── Webex platform service status (status.webex.com) ────────────── */}
      <section>
        <WebexStatusCard data={webex} />
      </section>

      {/* ── 3. Chart row — call-volume area + system-health donut ───────── */}
      <section className="grid gap-4 lg:grid-cols-3">
        <div className="card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold">Call volume — last 7 days</div>
            <Link to="/reports" className="text-xs text-tnh-accent hover:underline">Open Reports →</Link>
          </div>
          {!summary ? (
            <div className="h-44 grid place-items-center text-sm text-gray-500">Loading…</div>
          ) : (summary.daily_series ?? []).length === 0 ? (
            <div className="h-44 grid place-items-center text-sm text-gray-500">{summary.note}</div>
          ) : (
            <AreaChart series={summary.daily_series} />
          )}
        </div>
        <div className="card p-5">
          <div className="text-sm font-semibold mb-3">System health</div>
          <HealthDonut healthy={healthy} attention={attention} down={down} unknown={unknown} />
        </div>
      </section>

      {/* ── 4. All-modules grid ─────────────────────────────────────────── */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">All modules</h2>
          <span className="text-xs text-gray-500">{cards.length} cards · click to open</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
          {cards.map((c) => <ModuleCardTile key={c.key} card={c} />)}
        </div>
      </section>

      {/* ── 5. Bottom row — top destinations + recent alerts ────────────── */}
      <section className="grid gap-4 lg:grid-cols-2">
        <div className="card p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold">Top destinations — last 7 days</div>
            <Link to="/reports?tab=dashboard" className="text-xs text-tnh-accent hover:underline">Reports →</Link>
          </div>
          <div className="text-[11px] text-gray-500 -mt-2 mb-2">
            Top called numbers from CDR (proxy for top queues — true per-queue counts need WxCC Analyzer scope).
          </div>
          {!summary ? <div className="h-32 grid place-items-center text-sm text-gray-500">Loading…</div>
            : (summary.top_destinations ?? []).length === 0 ? <div className="h-32 grid place-items-center text-sm text-gray-500">No CDR rows yet.</div>
            : <BarList rows={summary.top_destinations.map((r) => ({ label: r.called_number, value: r.calls }))} />}
        </div>
        <div className="card p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold">Recent alerts</div>
            <Link to="/notifications" className="text-xs text-tnh-accent hover:underline">All notifications →</Link>
          </div>
          {!alerts ? <div className="h-32 grid place-items-center text-sm text-gray-500">Loading…</div>
            : alerts.length === 0 ? <div className="h-32 grid place-items-center text-sm text-gray-500">No unread alerts — all clear.</div>
            : <AlertsList rows={alerts.slice(0, 6)} />}
        </div>
      </section>
    </div>
  );
}

// ===========================================================================
//  Module-card derivation — one card per module, live metric + status dot.
// ===========================================================================
function deriveModuleCards(state: {
  snap: PublicSnapshot | null;
  summary: DashboardSummary | null;
  alerts: NotificationRow[] | null;
  unread: number | null;
  spokes: SpokeRow[] | null;
  lgwCerts: LgwCert[] | null;
  cubeCerts: CubeDeviceCert[] | null;
  qa: QaFinding[] | null;
  wxc: WxcStatus | null;
  wxcc: WxccStatus | null;
  gateways: LgwGateway[] | null;
  queues: WxccQueue[] | null;
  features: FeatureRequest[] | null;
  tickets: SupportTicket[] | null;
  tenants: TenantRow[] | null;
  licenses: LicenseRow[] | null;
  connectors: ConnectorHealth[] | null;
  me: Me;
}): ModuleCard[] {
  const { me, snap, summary, alerts, unread, spokes, lgwCerts, cubeCerts, qa, wxc, wxcc, gateways, queues, features, tickets, tenants, licenses, connectors } = state;

  // helpers
  const wxcCount = wxc?.configured ? (wxc.stats?.users_total ?? 0) : 0;
  const wxcErr = wxc?.configured ? Object.values(wxc.errors ?? {}).filter(Boolean).length : 0;
  const wxccCount = wxcc?.configured ? (wxcc.stats?.queues_total ?? 0) : 0;
  const wxccErr = wxcc?.configured ? Object.values(wxcc.errors ?? {}).filter(Boolean).length : 0;
  // last_heartbeat_at is epoch MILLISECONDS (spoke-svc writes Date.now(); Spokes.tsx
  // and spoke-svc deriveStatusPill both treat it as ms) — do NOT multiply by 1000.
  const spokeHealthy = (spokes ?? []).filter((s) => s.last_heartbeat_at && (Date.now() - s.last_heartbeat_at) < 5 * 60 * 1000).length;
  const spokeTotal = (spokes ?? []).length;
  const certExpiring = [...(lgwCerts ?? []), ...(cubeCerts ?? [])].filter((c: any) => c.days_until_expiry != null && c.days_until_expiry > 0 && c.days_until_expiry <= 30).length;
  const certExpired  = [...(lgwCerts ?? []), ...(cubeCerts ?? [])].filter((c: any) => c.days_until_expiry != null && c.days_until_expiry <= 0).length;
  const cubeReachable = (gateways ?? []).filter((g) => g.status === 'reachable').length;
  const cubeTotal = (gateways ?? []).length;
  const qaCritical = (qa ?? []).filter((f) => f.severity === 'critical' || f.severity === 'error').length;
  const featCount = (features ?? []).length;
  const ticketCount = (tickets ?? []).length;
  const tenantCount = (tenants ?? []).length;
  const license = (licenses ?? []).find((l) => l.status === 'active') ?? (licenses ?? [])[0];
  const licDays = license ? Math.floor((license.end_date - Date.now()) / 86400000) : null;
  const platformTier = snap?.overall ?? 'operational';

  // Tunnel connectors — the Cloudflare Tunnel connector replaces the legacy
  // Docker spoke for CUCM/UCCX reachability. Driven by D1 connector_status.
  const connList = connectors ?? [];
  const connTotal = connList.length;
  const cucmConnOnline = connList.filter((c) => c.cucm_reachable && !c.stale).length;
  const uccxConnOnline = connList.filter((c) => c.uccx_reachable && !c.stale).length;
  const connDown = connList.filter((c) => c.tier === 'major_outage').length;
  const connStale = connList.filter((c) => c.stale).length;
  const newestConn = connList.reduce<ConnectorHealth | null>(
    (acc, c) => (!acc || c.last_checked_at > acc.last_checked_at ? c : acc), null);
  const tunnelStatus: ModuleStatus = connectors == null ? 'unknown'
    : connTotal === 0 ? 'attention'
    : connDown > 0 ? 'down' : connStale > 0 ? 'attention' : 'healthy';

  // status derivation
  const wxcStatus: ModuleStatus = !wxc ? 'unknown' : !wxc.configured ? 'attention' : wxcErr > 0 ? 'attention' : 'healthy';
  const wxccStatus: ModuleStatus = !wxcc ? 'unknown' : !wxcc.configured ? 'attention' : wxccErr > 0 ? 'attention' : 'healthy';
  const spokeStatus: ModuleStatus = spokes ? (spokeTotal === 0 ? 'attention' : spokeHealthy === spokeTotal ? 'healthy' : 'attention') : (connTotal > 0 ? 'healthy' : 'unknown');
  const lgwStatus: ModuleStatus = !lgwCerts && !cubeCerts ? 'unknown' : certExpired > 0 ? 'down' : certExpiring > 0 ? 'attention' : 'healthy';
  const cubeStatus: ModuleStatus = !gateways ? 'unknown' : cubeTotal === 0 ? 'attention' : cubeReachable === cubeTotal ? 'healthy' : 'attention';
  // CUCM/UCCX prefer the tunnel connector when one exists; otherwise fall back
  // to the legacy spoke heartbeat so older tenants still render.
  const cucmStatus: ModuleStatus = connTotal > 0
    ? (cucmConnOnline > 0 ? 'healthy' : connStale > 0 ? 'attention' : 'down')
    : !spokes ? 'unknown' : spokeTotal === 0 ? 'attention' : spokeHealthy > 0 ? 'healthy' : 'attention';
  const uccxStatus: ModuleStatus = connTotal > 0
    ? (uccxConnOnline > 0 ? 'healthy' : connStale > 0 ? 'attention' : 'down')
    : !spokes ? 'unknown' : spokeTotal === 0 ? 'attention' : spokeHealthy > 0 ? 'healthy' : 'attention';
  // Match the "Open alerts" KPI tile: ANY unread → attention (amber); only zero is healthy.
  const alertStatus: ModuleStatus = unread == null ? 'unknown' : unread > 0 ? 'attention' : 'healthy';
  const qaStatus: ModuleStatus = qa == null ? 'unknown' : qaCritical > 0 ? 'attention' : 'healthy';
  const statusModuleStatus: ModuleStatus = !snap ? 'unknown' : platformTier === 'operational' ? 'healthy' : platformTier === 'degraded' ? 'attention' : 'down';
  const licStatus: ModuleStatus = !license ? 'unknown' : license.status !== 'active' ? 'attention' : (licDays != null && licDays < 30) ? 'attention' : 'healthy';

  return [
    // CORE MODULES
    { key: 'wxc', label: 'Webex Calling', group: 'Core', to: '/modules/wxc', status: wxcStatus,
      metric: wxc ? (wxc.configured ? `${wxcCount} users` : 'not configured') : '…',
      helper: wxc?.configured ? `${wxc.stats?.numbers_total ?? 0} numbers · ${wxc.stats?.queues_total ?? 0} queues` : 'add Service App creds' },
    { key: 'wxcc', label: 'Webex Contact Ctr', group: 'Core', to: '/modules/wxcc', status: wxccStatus,
      metric: wxcc ? (wxcc.configured ? `${wxccCount} queues` : 'not configured') : '…',
      helper: wxcc?.configured ? `${wxcc.stats?.agents_total ?? 0} agents · ${wxcc.stats?.sites_total ?? 0} sites` : 'add WxCC org creds' },
    { key: 'cucm', label: 'CUCM', group: 'Core', to: '/modules/cucm', status: cucmStatus,
      metric: connTotal > 0 ? (cucmConnOnline > 0 ? (newestConn?.cucm_version || 'reachable') : 'unreachable')
        : spokes ? `${spokeHealthy}/${spokeTotal} spokes` : '…',
      helper: connTotal > 0 ? `AXL via tunnel · ${cucmConnOnline}/${connTotal} online`
        : spokeTotal > 0 ? 'AXL via spoke' : 'no connector registered' },
    { key: 'uccx', label: 'UCCX', group: 'Core', to: '/modules/uccx', status: uccxStatus,
      metric: connTotal > 0 ? (uccxConnOnline > 0 ? 'reachable' : 'unreachable')
        : spokes ? `${spokeHealthy}/${spokeTotal} spokes` : '…',
      helper: connTotal > 0 ? `REST via tunnel · ${uccxConnOnline}/${connTotal} online` : 'REST via spoke' },
    { key: 'lgw', label: 'Local Gateway', group: 'Core', to: '/modules/lgw', status: lgwStatus,
      metric: gateways ? `${cubeTotal} gateways` : '…',
      helper: certExpired > 0 ? `${certExpired} cert(s) expired` : certExpiring > 0 ? `${certExpiring} expiring ≤30d` : 'certs healthy' },
    { key: 'cube', label: 'CUBE', group: 'Core', to: '/modules/cube', status: cubeStatus,
      metric: gateways ? `${cubeReachable}/${cubeTotal} reachable` : '…',
      helper: cubeCerts ? `${cubeCerts.length} on-box cert(s)` : 'spoke push pending' },

    // OPERATIONS
    { key: 'troubleshooting', label: 'Troubleshooting', group: 'Ops', to: '/troubleshooting',
      status: (alerts && alerts.length > 0) ? 'attention' : 'healthy',
      metric: '5 sub-tabs', helper: 'WxC · WxCC · CUCM · UCCX · LGW' },
    { key: 'reports', label: 'Reports', group: 'Ops', to: '/reports',
      status: summary ? 'healthy' : 'unknown',
      metric: summary ? `${summary.totals.calls_today} calls today` : '…',
      helper: '9 report tabs' },
    { key: 'notifications', label: 'Notifications', group: 'Ops', to: '/notifications',
      status: alertStatus,
      metric: unread != null ? `${unread} unread` : '…',
      helper: queues != null ? 'live signal feed' : 'live feed' },

    // PLATFORM
    { key: 'spokes', label: 'Spokes', group: 'Platform', to: '/spokes', status: spokeStatus,
      metric: spokes ? `${spokeTotal} registered` : (connTotal > 0 ? 'tunnel mode' : '…'),
      helper: spokes ? `${spokeHealthy} heartbeating` : (connTotal > 0 ? 'Cloudflare Tunnel · no Docker spoke' : 'list loading') },
    { key: 'tunnel', label: 'Tunnel Connectors', group: 'Platform', to: '/spokes', status: tunnelStatus,
      metric: connectors == null ? '…' : connTotal === 0 ? 'none registered' : `${connTotal - connStale}/${connTotal} online`,
      helper: connectors == null ? 'connector health'
        : newestConn ? `last checked ${fmtAge(newestConn.last_checked_at)}` : 'Cloudflare Tunnel (CUCM/UCCX)' },
    { key: 'configuration', label: 'Config Mgr', group: 'Platform', to: '/configuration',
      status: (wxc?.configured || wxcc?.configured) ? 'healthy' : 'attention',
      metric: (wxc?.configured ? 1 : 0) + (wxcc?.configured ? 1 : 0) + ((spokes ?? []).length > 0 ? 1 : 0) + ' categories',
      helper: 'WxC / WxCC / CUCM / UCCX' },
    { key: 'features', label: 'Features', group: 'Platform', to: '/features',
      status: featCount > 0 ? 'attention' : 'healthy',
      metric: features ? `${featCount} pending` : '…',
      helper: 'feature requests' },
    { key: 'support', label: 'Support', group: 'Platform', to: '/support',
      status: ticketCount > 0 ? 'attention' : 'healthy',
      metric: tickets ? `${ticketCount} awaiting` : '…',
      helper: 'support tickets' },
    { key: 'qa', label: 'QA Sentry', group: 'Platform', to: '/qa', status: qaStatus,
      metric: qa ? `${qa.length} recent` : '…',
      helper: qaCritical > 0 ? `${qaCritical} critical` : 'no critical findings' },

    // ADMIN
    { key: 'tenants', label: 'Tenants', group: 'Admin', to: '/admin/tenants',
      status: tenants ? 'healthy' : (me.role === 'super_admin' ? 'unknown' : 'healthy'),
      metric: tenants ? `${tenantCount} tenant${tenantCount === 1 ? '' : 's'}` : me.tenant_id,
      helper: me.role === 'super_admin' ? 'CRUD + license' : 'your tenant' },
    { key: 'license', label: 'License', group: 'Admin', to: '/admin/license', status: licStatus,
      metric: license ? `${license.term_years}y · ${licDays}d left` : '…',
      helper: license ? `${(license.plan_amount / 100).toLocaleString('en-US', { style: 'currency', currency: license.currency })}/yr` : 'no active license' },
    { key: 'audit', label: 'Audit', group: 'Admin', to: '/audit',
      status: 'healthy', metric: 'append-only', helper: 'tamper-evident log' },
    { key: 'status', label: 'Status', group: 'Admin', to: '/status', status: statusModuleStatus,
      metric: snap ? snap.overall.replace('_', ' ') : '…',
      helper: snap ? `${Object.keys(snap.services).length} services tracked` : 'public status feed' },
  ];
}

// ===========================================================================
//  Subcomponents — match the existing TriNetHub card / pill / chart patterns.
// ===========================================================================
function fmtDur(sec: number | null | undefined): string {
  if (sec == null || !isFinite(sec) || sec === 0) return '0s';
  if (sec < 60) return `${Math.round(sec)}s`;
  const m = Math.floor(sec / 60), s = Math.round(sec % 60);
  return s ? `${m}m ${s}s` : `${m}m`;
}

// Human "x ago" from an epoch-ms timestamp (connector_status.last_checked_at).
function fmtAge(epochMs: number | null | undefined): string {
  if (epochMs == null || !isFinite(epochMs) || epochMs === 0) return 'never';
  const s = Math.floor((Date.now() - epochMs) / 1000);
  if (s < 0) return 'just now';
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function KpiSpark({ label, value, sub, tone, spark }: {
  label: string; value: string; sub?: string;
  tone: 'ok' | 'warn' | 'err'; spark: number[];
}) {
  const accent = tone === 'err' ? 'text-tnh-err' : tone === 'warn' ? 'text-tnh-warn' : '';
  const stroke = tone === 'err' ? '#b00020' : tone === 'warn' ? '#a16207' : '#1f8a4d';
  return (
    <div className="card p-4">
      <div className="text-[11px] text-gray-500 uppercase tracking-wide">{label}</div>
      <div className={`text-2xl font-semibold mt-1 ${accent}`}>{value}</div>
      {sub && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
      <Sparkline points={spark} stroke={stroke} />
    </div>
  );
}

function Sparkline({ points, stroke }: { points: number[]; stroke: string }) {
  const W = 180, H = 32;
  if (!points || points.length < 2) {
    return <div className="text-[10px] text-gray-400 mt-2 h-8 grid place-items-center">no series yet</div>;
  }
  const max = Math.max(1, ...points);
  const step = W / Math.max(1, points.length - 1);
  const poly = points.map((p, i) => `${(i * step).toFixed(1)},${(H - (p / max) * H).toFixed(1)}`).join(' ');
  const area = `${poly} ${W.toFixed(1)},${H} 0,${H}`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-8 mt-2 block">
      <polygon points={area} fill={stroke} fillOpacity="0.10" />
      <polyline points={poly} fill="none" stroke={stroke} strokeWidth="1.5" />
    </svg>
  );
}

function AreaChart({ series }: { series: Array<{ day: string; calls: number; completed: number; abandoned: number }> }) {
  const W = 720, H = 200, P = 28;
  const max = Math.max(1, ...series.map((d) => d.calls));
  const xStep = (W - 2 * P) / Math.max(1, series.length - 1);
  const callsPoly = series.map((d, i) => `${(P + i * xStep).toFixed(1)},${(H - P - (d.calls / max) * (H - 2 * P)).toFixed(1)}`).join(' ');
  const area = `${P},${H - P} ${callsPoly} ${(P + (series.length - 1) * xStep).toFixed(1)},${H - P}`;
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
        {/* y-axis ticks */}
        {[0.25, 0.5, 0.75, 1].map((f) => (
          <line key={f} x1={P} x2={W - P} y1={H - P - f * (H - 2 * P)} y2={H - P - f * (H - 2 * P)} stroke="currentColor" strokeOpacity="0.08" />
        ))}
        <line x1={P} y1={H - P} x2={W - P} y2={H - P} stroke="currentColor" strokeOpacity="0.2" />
        <polygon points={area} fill="#0064ff" fillOpacity="0.12" />
        <polyline points={callsPoly} fill="none" stroke="#0064ff" strokeWidth="2" />
        {/* completed/abandoned stacked thin lines */}
        <polyline
          points={series.map((d, i) => `${(P + i * xStep).toFixed(1)},${(H - P - (d.abandoned / max) * (H - 2 * P)).toFixed(1)}`).join(' ')}
          fill="none" stroke="#b00020" strokeWidth="1" strokeOpacity="0.5" strokeDasharray="3,3" />
        {/* x labels — first + middle + last */}
        {series.length > 0 && (
          <>
            <text x={P} y={H - 6} fontSize="10" fill="currentColor" opacity="0.55">{series[0]!.day.slice(5)}</text>
            <text x={W - P} y={H - 6} fontSize="10" textAnchor="end" fill="currentColor" opacity="0.55">{series[series.length - 1]!.day.slice(5)}</text>
          </>
        )}
        <text x={P + 4} y={P} fontSize="10" fill="currentColor" opacity="0.55">max {max}</text>
      </svg>
      <div className="flex gap-4 text-xs text-gray-500 mt-1">
        <span className="flex items-center gap-1.5"><span className="w-3 h-1 rounded-full bg-tnh-accent" /> calls</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-1 rounded-full bg-tnh-err opacity-60" /> abandoned</span>
      </div>
    </div>
  );
}

function HealthDonut({ healthy, attention, down, unknown }: { healthy: number; attention: number; down: number; unknown: number }) {
  const total = Math.max(1, healthy + attention + down + unknown);
  const slices = [
    { value: healthy,   color: '#1f8a4d', label: 'healthy' },
    { value: attention, color: '#a16207', label: 'attention' },
    { value: down,      color: '#b00020', label: 'down' },
    { value: unknown,   color: '#9ca3af', label: 'unknown' },
  ].filter((s) => s.value > 0);
  const R = 56, r = 38, CX = 80, CY = 80, C = 2 * Math.PI * R;
  let off = 0;
  const arcs = slices.map((s) => {
    const len = (s.value / total) * C;
    const seg = (
      <circle key={s.label} cx={CX} cy={CY} r={R} fill="none" stroke={s.color} strokeWidth={R - r}
              strokeDasharray={`${len} ${C - len}`} strokeDashoffset={-off} transform={`rotate(-90 ${CX} ${CY})`} />
    );
    off += len;
    return seg;
  });
  return (
    <div className="flex items-center gap-4">
      <svg viewBox="0 0 160 160" className="w-32 h-32 shrink-0">
        <circle cx={CX} cy={CY} r={R} fill="none" stroke="currentColor" strokeOpacity="0.08" strokeWidth={R - r} />
        {arcs}
        <text x={CX} y={CY - 4} textAnchor="middle" fontSize="22" fontWeight="600" fill="currentColor">{total}</text>
        <text x={CX} y={CY + 14} textAnchor="middle" fontSize="9" fill="currentColor" opacity="0.6">modules</text>
      </svg>
      <ul className="space-y-1.5 text-xs">
        {[
          { v: healthy,   color: 'bg-tnh-ok',  label: 'Healthy' },
          { v: attention, color: 'bg-tnh-warn',label: 'Attention' },
          { v: down,      color: 'bg-tnh-err', label: 'Down' },
          { v: unknown,   color: 'bg-gray-400',label: 'Unknown' },
        ].map((s) => (
          <li key={s.label} className="flex items-center gap-2">
            <span className={`w-2.5 h-2.5 rounded-full ${s.color}`} />
            <span className="text-gray-600 dark:text-gray-300">{s.label}</span>
            <span className="ml-auto font-mono">{s.v}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ModuleCardTile({ card }: { card: ModuleCard }) {
  const dot = card.status === 'healthy' ? 'bg-tnh-ok'
    : card.status === 'attention' ? 'bg-tnh-warn'
    : card.status === 'down'      ? 'bg-tnh-err'
    : 'bg-gray-400';
  return (
    <Link to={card.to} className="card p-3 hover:border-tnh-accent transition group block">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-xs uppercase tracking-wide text-gray-500">{card.group}</div>
          <div className="font-medium text-sm mt-0.5 truncate">{card.label}</div>
        </div>
        <span className={`w-2 h-2 rounded-full ${dot} mt-1.5`} title={card.status} />
      </div>
      <div className="mt-2 text-base font-mono truncate" title={card.metric}>{card.metric}</div>
      {card.helper && <div className="text-[11px] text-gray-500 mt-0.5 truncate" title={card.helper}>{card.helper}</div>}
    </Link>
  );
}

function BarList({ rows }: { rows: Array<{ label: string; value: number }> }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <ul className="space-y-2">
      {rows.map((r, i) => (
        <li key={i}>
          <div className="flex items-center justify-between text-xs">
            <span className="font-mono truncate mr-2">{r.label || '—'}</span>
            <span className="text-gray-500 shrink-0">{r.value}</span>
          </div>
          <div className="mt-1 h-2 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
            <div className="h-full bg-tnh-accent" style={{ width: `${(r.value / max) * 100}%` }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

function AlertsList({ rows }: { rows: NotificationRow[] }) {
  return (
    <ul className="space-y-2">
      {rows.map((n) => {
        const sev = ((n as any).severity ?? 'info') as string;
        const dot = sev === 'critical' || sev === 'error' ? 'bg-tnh-err'
          : sev === 'warn' || sev === 'warning' ? 'bg-tnh-warn' : 'bg-tnh-accent';
        return (
          <li key={n.notification_id} className="flex items-start gap-2 text-xs">
            <span className={`mt-1.5 w-1.5 h-1.5 rounded-full ${dot} shrink-0`} />
            <div className="min-w-0 flex-1">
              <div className="font-medium truncate" title={n.title || n.kind}>{n.title || n.kind}</div>
              <div className="text-gray-500 truncate">{n.body || ''}</div>
              <div className="text-[10px] text-gray-400">{n.created_at ? new Date(n.created_at).toLocaleString() : ''}</div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

// Webex platform service status (status.webex.com). Green when no monitored
// (Contact Center / Calling) incident is open; amber/red with details otherwise.
function WebexStatusCard({ data }: { data: WebexServiceStatus | null }) {
  const page = data?.status_page ?? 'https://status.webex.com/commercial/status';
  const open = data?.open ?? 0;
  const worst = (data?.incidents ?? []).reduce<'info' | 'warn' | 'error' | 'critical'>((acc, i) => {
    const rank = { info: 0, warn: 1, error: 2, critical: 3 } as const;
    return rank[i.severity] > rank[acc] ? i.severity : acc;
  }, 'info');
  const tone = !data ? 'unknown' : open === 0 ? 'ok' : (worst === 'critical' || worst === 'error') ? 'err' : 'warn';
  const dot = tone === 'ok' ? 'bg-tnh-ok' : tone === 'err' ? 'bg-tnh-err' : tone === 'warn' ? 'bg-tnh-warn' : 'bg-gray-400';
  const headline = !data ? 'Checking Webex service status…'
    : open === 0 ? 'All monitored Webex services operational'
    : `${open} Webex service incident${open > 1 ? 's' : ''} in progress`;
  return (
    <div className="card p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`w-2.5 h-2.5 rounded-full ${dot} shrink-0`} />
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate">Webex service status — {headline}</div>
            <div className="text-[11px] text-gray-500">Monitors Cisco Webex Contact Center &amp; Calling · status.webex.com</div>
          </div>
        </div>
        <a href={page} target="_blank" rel="noopener noreferrer" className="text-xs text-tnh-accent hover:underline shrink-0">Live status →</a>
      </div>
      {open > 0 && (
        <ul className="mt-3 space-y-1.5">
          {(data?.incidents ?? []).map((i, idx) => {
            const d = i.severity === 'critical' || i.severity === 'error' ? 'bg-tnh-err' : i.severity === 'warn' ? 'bg-tnh-warn' : 'bg-tnh-accent';
            return (
              <li key={idx} className="flex items-start gap-2 text-xs">
                <span className={`mt-1.5 w-1.5 h-1.5 rounded-full ${d} shrink-0`} />
                <div className="min-w-0">
                  <div className="font-medium truncate" title={i.title}>{i.title}</div>
                  <div className="text-gray-500">{i.state}{i.regions ? ` · ${i.regions}` : ''}</div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
